import { Fragment } from 'react';
import PropTypes from 'prop-types';

import { useTheme } from '@mui/material/styles';
import { Box, Divider, List, ListSubheader } from '@mui/material';
import navConfig from './navConfig';
import NavItem from './NavItem/NavItem';
import { AgentFab } from '../../features/cometeOnTime/AgentFab';

import useAuth from '../../../hooks/useAuth';
import useCollapseDrawer from '../../../hooks/useCollapseDrawer';
import useLocales from '../../../hooks/useLocales';
import { useSelector } from '../../../redux/store';
import { useHasPermission, useAdminStore } from '../../../store/useAdminStore';

const NavSection = ({ isMobile = false }) => {
  const { env, user, features, isAuthenticated } = useAuth();
  const { translate } = useLocales();
  // CW-21903 : `useHasPermission` souscrit aux permissions et au rôle dans
  // Zustand → ce composant re-render dès que `setPermissions` ou `clear` est
  // appelé. Sans cette souscription, le menu restait sur l'état "permissions
  // vides" du 1er render jusqu'à un reload manuel.
  const hasPermission = useHasPermission();
  // CW-21903 (suite review) : `isReady` passe à true uniquement quand les
  // permissions sont arrivées (depuis /login ou /mce/my-permissions). Tant
  // qu'on ne les a pas pour un user authentifié, on ne render PAS la nav
  // plutôt que d'afficher un menu incomplet qui se complète ensuite (flash
  // visible sur la section MCE en page reload via auto-login).
  const isReady = useAdminStore((s) => s.isReady);
  const config = navConfig.get(env, user, features, translate, hasPermission);
  const { isCollapse } = useCollapseDrawer();
  const { numbers } = useSelector((state) => state.missions);
  const theme = useTheme();

  if (isAuthenticated && !isReady) {
    return null;
  }

  if (!config) {
    return null;
  }

  // Bottom navigation
  if (isMobile) {
    const { items } = config[0];
    return items.map((item) => {
      if (item.isFab) {
        return <AgentFab key="ontime-fab" />;
      }
      return (
        <NavItem
          key={item.title}
          item={item}
          isMobile={isMobile}
          number={item.title === 'Missions' ? numbers.new.unseen : 0}
        />
      );
    });
  }

  // Side navigation
  return (
    <>
      {config.map((list, index) => {
        const { items, subheader } = list;
        const isLastSection = index === config.length - 1;

        return (
          <Fragment key={subheader}>
            <List sx={styleList} disablePadding>
              {!isCollapse && !isMobile && (
                <ListSubheader disableSticky disableGutters sx={styleListItemSubheader(theme)}>
                  {subheader}
                </ListSubheader>
              )}

              {items
                .filter((item) => !item.isFab)
                .map((item) => (
                  <NavItem
                    key={item.title}
                    id={item.id}
                    item={item}
                    isCollapse={isCollapse}
                    isMobile={isMobile}
                    number={item.title === 'Missions' ? numbers.new.unseen : 0}
                  />
                ))}
            </List>

            {/* Divider entre les sections */}
            {!isLastSection && !isCollapse && (
              <Box sx={styleDividerWrapper}>
                <Divider sx={styleDivider(theme)} />
              </Box>
            )}
          </Fragment>
        );
      })}
    </>
  );
};

const styleList = {
  display: 'flex',
  flexFlow: 'column nowrap',
  gap: 0.25
};

const styleListItemSubheader = (theme) => ({
  fontFamily: theme.typography.fontFamily,
  fontWeight: 700,
  fontSize: '0.75rem',
  lineHeight: '1.125rem',
  letterSpacing: '0px',
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
  textAlign: 'left',
  pt: 2,
  pb: 0.25,
  px: 2,
  background: 'transparent'
});

const styleDividerWrapper = {
  display: 'flex',
  justifyContent: 'center',
  py: 0.75
};

const styleDivider = (theme) => ({
  width: '100%',
  height: '2px',
  borderColor: theme.palette.divider,
  borderBottomWidth: '2px'
});

NavSection.propTypes = {
  isMobile: PropTypes.bool
};

export default NavSection;
